// src/pages/SettingPage/tabs/TeacherManagementTab.js
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, message, Space,
  Select, Popconfirm, Upload, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  DownloadOutlined, UploadOutlined, InfoCircleOutlined
} from '@ant-design/icons';

import { db } from '../../../firebase-config';
import {
  collection, getDocs, setDoc, updateDoc, deleteDoc,
  doc, query, where, writeBatch
} from 'firebase/firestore';

// ===== セカンダリ認証（現在のセッションを切り替えずにアカウント作成するため） =====
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';

// ===== Cloud Functions（Auth・Firestore・Storage を削除） =====
import { getFunctions, httpsCallable } from 'firebase/functions';

import { downloadExcelTemplate } from '../../../utils/excelUtils';
import * as XLSX from 'xlsx';

//const { Title, Text } = Typography;
const { Option } = Select;

const TeacherManagementTab = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [form] = Form.useForm();

  const usersCol = collection(db, 'users');
  const rolesCol = collection(db, 'roles');

  // ---------- セカンダリ認証ヘルパー ----------
  const getSecondaryAuth = () => {
    const defaultApp = getApp();
    const existing = getApps().find((a) => a.name === 'Secondary');
    const secondaryApp = existing || initializeApp(defaultApp.options, 'Secondary');
    return getAuth(secondaryApp);
  };

  // ---------- 役割ヘルパー ----------
  const toKey = (v) => String(v ?? '').trim().toLowerCase();

  const buildRoleMaps = (roleList) => {
    const byId = new Map(roleList.map(r => [String(r.id), r]));
    const byName = new Map(roleList.map(r => [toKey(r.name), r]));
    const byDisplayName = new Map(roleList.map(r => [toKey(r.displayName), r]));
    const byTitle = new Map(roleList.map(r => [toKey(r.title), r]));
    const byCode = new Map(roleList.map(r => [toKey(r.code), r]));
    return { byId, byName, byDisplayName, byTitle, byCode };
  };

  const normalizeRoleDoc = (raw, maps) => {
    if (!raw) return null;
    const str = String(raw).trim();

    if (maps.byId.has(str)) return maps.byId.get(str);
    const k = toKey(str);
    return maps.byName.get(k)
      || maps.byDisplayName.get(k)
      || maps.byTitle.get(k)
      || maps.byCode.get(k)
      || null;
  };

  const roleNameOf = (roleDoc) => {
    if (!roleDoc) return '未設定';
    return roleDoc.name || roleDoc.displayName || roleDoc.title || '未設定';
  };

  // ---------- 取得 ----------
  const fetchUsersAndRoles = async () => {
    setLoading(true);
    try {
      const [usersSnap, rolesSnap] = await Promise.all([
        getDocs(query(usersCol, where('userType', '==', 'teacher'))),
        getDocs(rolesCol)
      ]);

      const teacherList = usersSnap.docs.map(d => ({ ...d.data(), id: d.id }));
      const roleList = rolesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

      const maps = buildRoleMaps(roleList);

      const processed = teacherList.map(t => {
        const raw = t.roleId ?? t.role?.id ?? t.role ?? t.roleName ?? t.code ?? null;
        const roleDoc = normalizeRoleDoc(raw, maps);
        return {
          ...t,
          roleId: roleDoc?.id ?? t.roleId ?? null,
          roleName: roleNameOf(roleDoc),
        };
      });

      setUsers(processed);
      setRoles(roleList);
    } catch (err) {
      console.error(err);
      message.error('データを読み込めませんでした。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersAndRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- モーダル ----------
  const showModal = (user = null) => {
    setEditingUser(user);
    form.resetFields();
    if (user) {
      form.setFieldsValue({
        nameRomaji: user.nameRomaji || '',
        homeroomClass: user.homeroomClass || '',
        email: user.email || '',
        roleId: user.roleId || null,
      });
    } else {
      form.setFieldsValue({
        nameRomaji: '',
        homeroomClass: '',
        email: '',
        roleId: null,
        password: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (!values.roleId) {
        message.error('有効な役割を選択してください。');
        return;
      }

      const data = {
        nameRomaji: values.nameRomaji,
        homeroomClass: values.homeroomClass || '',
        email: String(values.email).trim().toLowerCase(),
        roleId: values.roleId,
        userType: 'teacher',
      };

      if (editingUser) {
        // Firestore プロフィールを更新
        await updateDoc(doc(db, 'users', editingUser.id), data);
        message.success('教員を更新しました。');
      } else {
        // セカンダリ認証でアカウント作成し、users/{uid} に書き込み（Auth と同期）
        const secondaryAuth = getSecondaryAuth();
        try {
          const cred = await createUserWithEmailAndPassword(
            secondaryAuth,
            data.email,
            String(values.password || '').trim()
          );
          const uid = cred.user?.uid;
          const { password, ...rest } = data;

          // addDoc ではなく users/{uid} へ（id == uid）
          await setDoc(doc(db, 'users', uid), { ...rest, authUid: uid });
          message.success('教員を追加しました。');
        } catch (authErr) {
          console.error('Create user error:', authErr?.code, authErr);
          if (authErr?.code === 'auth/email-already-in-use') {
            message.error('このメールアドレスは既に使用されています。');
          } else if (authErr?.code === 'auth/weak-password') {
            message.error('パスワードが弱すぎます（6文字以上）。');
          } else {
            message.error('アカウント作成中にエラーが発生しました。');
          }
          return; // エラー時はモーダルを閉じない
        }
      }

      handleCancel();
      fetchUsersAndRoles();
    } catch (e) {
      if (e?.errorFields) return; // バリデーションエラー
      console.error(e);
      message.error('保存中にエラーが発生しました。');
    }
  };

  // ---------- 教員削除：Cloud Function を呼んで Auth + Firestore + Storage を削除 ----------
  const handleDelete = async (id) => {
    try {
      const record = users.find((u) => u.id === id);
      const uid = record?.authUid || id;          // authUid を優先、無ければ doc id
      const avatarPath = record?.avatarPath || ""; // 画像があれば

      // デプロイ済みの Cloud Function と同じリージョンを指定する必要があります
      const functions = getFunctions(undefined, "asia-northeast2");

      // 正しい関数名: deleteUserAccount
      const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");

      // サーバー側の正しいパラメータ名: authUid, firestoreId, avatarPath
      await deleteUserAccount({
        authUid: uid,
        firestoreId: id,
        avatarPath,
      });

      message.success("教員を削除しました（Auth・Firestore・Storage）。");
      fetchUsersAndRoles();
    } catch (err) {
      console.error("deleteUserAccount error:", err);
      // 本当に失敗した場合のフォールバック（必要であれば維持）
      try {
        await deleteDoc(doc(db, "users", id));
        message.warning(
          "Firestore のドキュメントを削除しました。Auth が削除されていない場合は Cloud Function を確認してください。"
        );
        fetchUsersAndRoles();
      } catch (e2) {
        console.error(e2);
        message.error("教員の削除中にエラーが発生しました。");
      }
    }
  };

  // ---------- テンプレート ----------
  const handleDownloadTemplate = () => {
    const templateData = [
      {
        nameRomaji: 'Tanaka Kyoko',
        homeroomClass: '会計101',
        email: 'teacher.tanaka@example.com',
        password: 'password456',
        roleId: '役割のID / 名前 / コードを入力（例：教師）',
      },
    ];
    downloadExcelTemplate(templateData, 'TeacherUploadTemplate');
  };

  // ---------- Excel アップロード ----------
  const handleExcelUpload = (file) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      setUploading(true);
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(worksheet);

        const roleSnap = await getDocs(rolesCol);
        const roleList = roleSnap.docs.map(d => ({ ...d.data(), id: d.id }));
        const maps = buildRoleMaps(roleList);

        const batch = writeBatch(db);
        const secondaryAuth = getSecondaryAuth();

        let ok = 0, fail = 0;

        // 行ごとに Auth を順次作成してエラーを拾いやすくする
        for (const row of json) {
          const email = String(row.email || '').trim().toLowerCase();
          const password = String(row.password || '').trim();

          if (!email || !password) { fail++; continue; }

          try {
            const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
            const uid = cred.user?.uid;

            const rawRole = row.roleId ?? row.role ?? row.roleName ?? row.code ?? null;
            const roleDoc = normalizeRoleDoc(rawRole, maps);

            const data = {
              nameRomaji: String(row.nameRomaji || '').trim(),
              homeroomClass: String(row.homeroomClass || '').trim(),
              email,
              userType: 'teacher',
              roleId: roleDoc?.id ?? null,
              authUid: uid,
            };

            // batch で users/{uid} に書き込み（id == uid）
            batch.set(doc(db, 'users', uid), data);
            ok++;
          } catch (err) {
            fail++;
            console.error('Excel create user error:', email, err?.code || err?.message);
          }
        }

        try {
          await batch.commit();
          if (fail) {
            message.warning(`完了：${ok} 行成功、${fail} 行失敗。`);
          } else {
            message.success(`アップロード成功：教員 ${ok} 名。`);
          }
        } catch (commitErr) {
          console.error(commitErr);
          message.error('一括保存中にエラーが発生しました。');
        }
      } catch (readErr) {
        console.error(readErr);
        message.error('無効な Excel ファイルです。');
      } finally {
        setUploading(false);
        fetchUsersAndRoles();
      }
    };

    reader.readAsBinaryString(file);
    return false; // AntD のデフォルトアップロードを抑止
  };

  const columns = [
    {
      title: '氏名',
      dataIndex: 'nameRomaji',
      key: 'nameRomaji',
      sorter: (a, b) => (a.nameRomaji || '').localeCompare(b.nameRomaji || ''),
    },
    { title: '担任クラス', dataIndex: 'homeroomClass', key: 'homeroomClass' },
    { title: '役割', dataIndex: 'roleName', key: 'roleName' },
    { title: 'メール(Login ID)', dataIndex: 'email', key: 'email' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space wrap>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>編集</Button>
          <Popconfirm
            title="教員を削除"
            description="この教員を削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button icon={<DeleteOutlined />} danger>削除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
          教員を追加
        </Button>

        <Upload beforeUpload={handleExcelUpload} showUploadList={false} accept=".xlsx,.xls">
          <Button icon={<UploadOutlined />} loading={uploading}>
            {uploading ? '処理中...' : 'Excel からアップロード'}
          </Button>
        </Upload>

        <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          テンプレートをダウンロード
        </Button>

        <Tooltip title="Excel の列: nameRomaji, homeroomClass, email, password, roleId（役割のID/名前/コードを入力可）">
          <InfoCircleOutlined style={{ cursor: 'help', fontSize: 16 }} />
        </Tooltip>
      </Space>

      <Table
        columns={columns}
        dataSource={users.map(u => ({ ...u, key: u.id }))}
        loading={loading}
        bordered
      />

      <Modal
        title={editingUser ? '教員情報を編集' : '新規教員を追加'}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={editingUser ? '更新' : '追加'}
        cancelText="キャンセル"
        forceRender
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nameRomaji"
            label="氏名"
            rules={[{ required: true, message: '氏名を入力してください。' }]}
          >
            <Input placeholder="例: Tanaka Kyoko" />
          </Form.Item>

          <Form.Item name="homeroomClass" label="担任クラス">
            <Input placeholder="例: 会計101" />
          </Form.Item>

          <Form.Item
            name="roleId"
            label="役割"
            rules={[{ required: true, message: '役割を選択してください。' }]}
          >
            <Select placeholder="役割を選択">
              {roles.map(role => (
                <Option key={role.id} value={role.id}>
                  {role.name || role.displayName || role.title || role.code || role.id}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="email"
            label="メール"
            rules={[
              { required: true, message: 'メールアドレスを入力してください。' },
              { type: 'email', message: 'メールアドレスが無効です。' },
            ]}
          >
            <Input disabled={!!editingUser} placeholder="teacher.tanaka@example.com" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="パスワード"
              rules={[
                { required: true, message: 'パスワードを入力してください。' },
                { min: 6, message: 'パスワードは6文字以上で入力してください。' },
              ]}
            >
              <Input.Password placeholder="6文字以上" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TeacherManagementTab;
